// ===================================
// TRENCH QUEST - Game Master API Service
// Calls Firebase Cloud Functions for AI generation
// ===================================

import { getFunctions, httpsCallable } from 'firebase/functions'
import type {
  GeneratedSituation,
  GeneratedOutcome,
  GeneratedImage,
  GameSession,
  PlayerAction,
  ConsequenceLog,
  RealMarketData,
} from '@/types'

const functions = getFunctions()

// Cloud function callables
const generateDynamicSituationFn = httpsCallable(functions, 'generateDynamicSituation')
const resolveSituationChoiceFn = httpsCallable(functions, 'resolveSituationChoice')
const generateSceneImageFn = httpsCallable(functions, 'generateSceneImage')

const initiateStorylineFn = httpsCallable(functions, 'initiateStoryline')
const progressStorylineFn = httpsCallable(functions, 'progressStoryline')
const generateStorylineClimaxFn = httpsCallable(functions, 'generateStorylineClimax')
const generateBackstoryFn = httpsCallable(functions, 'generateBackstory')
const generateGameRecapFn = httpsCallable(functions, 'generateGameRecap')

/**
 * Request types for cloud functions
 */
interface TokenHolding {
  symbol: string
  name: string
  amount: number
  currentPrice: number
  avgBuyPrice: number
}

interface DynamicSituationRequest {
  gameState: {
    portfolioValue: number
    cashBalance: number
    reputation: number
    currentLocation: string
    goalAmount: number
    progressPercent: number
    daysRemaining: number
    decisionCount: number
    maxDecisions: number
    holdings: TokenHolding[]
    traits: {
      degenScore: number
      trustScore: number
      alphaScore: number
      wealthScore: number
    }
  }
  recentActions: Array<{
    type: string
    description: string
    timestamp: string
  }>
  activeConsequences: Array<{
    flags: string[]
    summary: string
  }>
  previousSituationIds: string[]
  marketData: {
    solPrice: number
    marketSentiment: string
    topGainers: string[]
    topLosers: string[]
    trendingTokens: string[]
  }
  triggerContext?: string
}

interface SituationOutcomeRequest {
  situation: {
    id: string
    title: string
    description: string
    category: string
    mood: string
    characters: Array<{
      id: string
      name: string
      title: string
      personality: string[]
    }>
  }
  choice: {
    id: string
    text: string                                         // Player's free-form input
    hint?: string
    riskLevel?: 'safe' | 'moderate' | 'risky' | 'yolo'   // Optional - AI will assess
  }
  gameState: {
    portfolioValue: number
    cashBalance: number
    reputation: number
    decisionCount: number
    maxDecisions: number
    holdings: TokenHolding[]
    traits: {
      degenScore: number
      trustScore: number
      alphaScore: number
      wealthScore: number
    }
  }
  marketData: {
    solPrice: number
    marketSentiment: string
  }
}

interface ImageGenerationRequest {
  situation: {
    location: string
    mood: string
    description: string
    characters: Array<{ name: string; title: string }>
    visualTags: string[]
  }
  style?: 'pixel_art' | 'cinematic' | 'noir' | 'neon' | 'comic' | 'realistic' | 'vaporwave'
}

/**
 * Generate a dynamic situation via Cloud Function
 */
export async function generateSituationViaCloud(
  session: GameSession,
  recentActions: PlayerAction[],
  consequenceLog: ConsequenceLog[],
  previousSituationIds: string[],
  marketData: RealMarketData,
  triggerContext?: string
): Promise<GeneratedSituation> {
  // No hard cap - games end organically. This is just for pacing hints.
  const decisionCount = session.decisionCount || 0
  const request: DynamicSituationRequest = {
    gameState: {
      portfolioValue: session.player.portfolio.totalValue,
      cashBalance: session.player.portfolio.cashBalance,
      reputation: session.player.reputation,
      currentLocation: session.world.currentLocation,
      goalAmount: session.goal.targetAmount,
      progressPercent: (session.player.portfolio.totalValue / session.goal.targetAmount) * 100,
      daysRemaining: session.goal.daysRemaining,
      decisionCount: decisionCount,
      maxDecisions: 0, // No hard cap - games end organically
      holdings: session.player.portfolio.holdings.map(h => ({
        symbol: h.symbol,
        name: h.name,
        amount: h.amount,
        currentPrice: h.currentPrice,
        avgBuyPrice: h.avgBuyPrice,
      })),
      traits: {
        degenScore: session.player.traits.degenScore,
        trustScore: session.player.traits.trustScore,
        alphaScore: session.player.traits.alphaScore,
        wealthScore: session.player.traits.wealthScore,
      },
    },
    recentActions: recentActions.slice(0, 10).map((a) => ({
      type: a.type,
      description: a.description,
      timestamp: a.timestamp.toISOString(),
    })),
    activeConsequences: consequenceLog
      .filter((c) => !c.resolved)
      .map((c) => ({
        flags: c.flags,
        summary: c.summary,
      })),
    previousSituationIds,
    marketData: {
      solPrice: marketData.solPrice,
      marketSentiment: marketData.marketSentiment,
      topGainers: marketData.topGainers.slice(0, 5).map((t) => `${t.symbol} (+${t.change24h.toFixed(1)}%)`),
      topLosers: marketData.topLosers.slice(0, 5).map((t) => `${t.symbol} (${t.change24h.toFixed(1)}%)`),
      trendingTokens: marketData.trendingTokens.slice(0, 5).map((t) => t.symbol),
    },
    triggerContext,
  }

  const result = await generateDynamicSituationFn(request)
  return result.data as GeneratedSituation
}

/**
 * Resolve a player's choice via Cloud Function
 */
export async function resolveChoiceViaCloud(
  situation: GeneratedSituation,
  choiceId: string,
  session: GameSession,
  marketData: RealMarketData
): Promise<GeneratedOutcome> {
  const choice = situation.choices?.find((c) => c.id === choiceId)
  if (!choice) {
    throw new Error(`Invalid choice: ${choiceId}. Situation may not have pre-generated choices.`)
  }

  const decisionCount = session.decisionCount || 0
  const request: SituationOutcomeRequest = {
    situation: {
      id: situation.id,
      title: situation.title,
      description: situation.description,
      category: situation.category,
      mood: situation.mood,
      characters: situation.characters.map((c) => ({
        id: c.id,
        name: c.name,
        title: c.title,
        personality: c.personality,
      })),
    },
    choice: {
      id: choice.id,
      text: choice.text,
      hint: choice.hint,
      riskLevel: choice.riskLevel,
    },
    gameState: {
      portfolioValue: session.player.portfolio.totalValue,
      cashBalance: session.player.portfolio.cashBalance,
      reputation: session.player.reputation,
      decisionCount: decisionCount,
      maxDecisions: 0, // No hard cap - games end organically
      holdings: session.player.portfolio.holdings.map(h => ({
        symbol: h.symbol,
        name: h.name,
        amount: h.amount,
        currentPrice: h.currentPrice,
        avgBuyPrice: h.avgBuyPrice,
      })),
      traits: {
        degenScore: session.player.traits.degenScore,
        trustScore: session.player.traits.trustScore,
        alphaScore: session.player.traits.alphaScore,
        wealthScore: session.player.traits.wealthScore,
      },
    },
    marketData: {
      solPrice: marketData.solPrice,
      marketSentiment: marketData.marketSentiment,
    },
  }

  const result = await resolveSituationChoiceFn(request)
  return result.data as GeneratedOutcome
}

/**
 * Generate a scene image via Cloud Function (SiliconFlow FLUX)
 * Returns null if image generation fails or is unavailable
 */
export async function generateImageViaCloud(
  situation: GeneratedSituation,
  style: 'pixel_art' | 'cinematic' | 'noir' | 'neon' | 'comic' | 'realistic' | 'vaporwave' = 'pixel_art'
): Promise<GeneratedImage | null> {
  const request: ImageGenerationRequest = {
    situation: {
      location: situation.location,
      mood: situation.mood,
      description: situation.description,
      characters: situation.characters.map((c) => ({
        name: c.name,
        title: c.title,
      })),
      visualTags: situation.visualTags,
    },
    style,
  }

  const result = await generateSceneImageFn(request)
  const data = result.data as { imageUrl: string | null }

  if (!data.imageUrl) return null

  return {
    id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    url: data.imageUrl,
    visualTags: situation.visualTags,
    situationId: situation.id,
    createdAt: new Date(),
    expiresAt: new Date(Date.now() + 60 * 60 * 1000), // URL valid for 1 hour
  }
}

/**
 * Check if cloud functions are available
 * Falls back to local generation if not
 */
export async function isCloudAvailable(): Promise<boolean> {
  try {
    // Simple health check - in production you might have a dedicated endpoint
    // For now we just check if functions are configured
    return typeof functions !== 'undefined'
  } catch {
    return false
  }
}

// ===================================
// MULTI-PART STORYLINE FUNCTIONS
// ===================================

import type {
  Storyline,
  StorylineTheme,
  StorylineBranch,
  StorylineInitiator,
} from '@/types'

/**
 * Initiate a new storyline via Cloud Function
 */
export async function initiateStorylineViaCloud(
  trigger: StorylineInitiator,
  session: GameSession,
  activeConsequences: ConsequenceLog[],
  existingStorylines: Storyline[]
): Promise<{
  storyline: Storyline
  openingSituation: GeneratedSituation
}> {
  const request = {
    trigger: {
      type: trigger.type,
      source: trigger.source,
      priority: trigger.priority,
    },
    gameState: {
      portfolioValue: session.player.portfolio.totalValue,
      reputation: session.player.reputation,
      degenScore: session.player.traits.degenScore,
      trustScore: session.player.traits.trustScore,
      location: session.world.currentLocation,
    },
    activeConsequences: activeConsequences
      .filter((c) => !c.resolved)
      .map((c) => ({
        flags: c.flags,
        summary: c.summary,
      })),
    existingStorylines: existingStorylines.map((s) => ({
      id: s.id,
      title: s.title,
      theme: s.theme,
      status: s.status,
    })),
  }

  const result = await initiateStorylineFn(request)
  const data = result.data as {
    id: string
    title: string
    description: string
    theme: StorylineTheme
    totalChapters: number
    primaryCharacters: string[]
    antagonist?: string
    stakes: string
    potentialReward: {
      type: 'cash' | 'reputation' | 'trait' | 'item' | 'location' | 'relationship'
      description: string
      value?: number
    }
    openingSituation: GeneratedSituation
    flags: string[]
  }

  const storyline: Storyline = {
    id: data.id,
    title: data.title,
    description: data.description,
    theme: data.theme,
    status: 'active',
    currentChapter: 1,
    totalChapters: data.totalChapters,
    situationIds: [data.openingSituation.id],
    primaryCharacters: data.primaryCharacters,
    antagonist: data.antagonist,
    stakes: data.stakes,
    potentialReward: data.potentialReward,
    flags: data.flags,
    startedAt: new Date(),
    createdAt: new Date(),
    updatedAt: new Date(),
  }

  return {
    storyline,
    openingSituation: data.openingSituation,
  }
}

/**
 * Progress an existing storyline via Cloud Function
 */
export async function progressStorylineViaCloud(
  storyline: Storyline,
  lastSituation: GeneratedSituation,
  lastChoice: string,
  lastOutcome: string,
  session: GameSession
): Promise<{
  situation: GeneratedSituation
  chapterProgress: {
    completed: boolean
    objective: string
    progress: string
  }
  isClimactic: boolean
  isBranchPoint: boolean
  branch?: StorylineBranch
  narrativeUpdate: string
}> {
  const request = {
    storyline: {
      id: storyline.id,
      title: storyline.title,
      theme: storyline.theme,
      currentChapter: storyline.currentChapter,
      totalChapters: storyline.totalChapters,
      stakes: storyline.stakes,
      previousSituations: storyline.situationIds.map((id, index) => ({
        id,
        title: `Situation ${index + 1}`,
        choiceMade: 'Previous choice',
      })),
    },
    lastSituation: {
      id: lastSituation.id,
      title: lastSituation.title,
      description: lastSituation.description,
    },
    lastChoice,
    lastOutcome,
    gameState: {
      portfolioValue: session.player.portfolio.totalValue,
      reputation: session.player.reputation,
      degenScore: session.player.traits.degenScore,
    },
  }

  const result = await progressStorylineFn(request)
  return result.data as {
    situation: GeneratedSituation
    chapterProgress: {
      completed: boolean
      objective: string
      progress: string
    }
    isClimactic: boolean
    isBranchPoint: boolean
    branch?: StorylineBranch
    narrativeUpdate: string
  }
}

/**
 * Generate the climactic conclusion to a storyline via Cloud Function
 */
export async function generateStorylineClimaxViaCloud(
  storyline: Storyline,
  journeySummary: string,
  keyCharacters: Array<{ id: string; name: string; role: string }>,
  keyChoices: string[]
): Promise<{
  situation: GeneratedSituation
  epilogue: {
    success: {
      narrative: string
      rewards: Record<string, unknown>
      newOpportunities: string[]
    }
    failure: {
      narrative: string
      consequences: Record<string, unknown>
      recoveryPath: string
    }
  }
  characterResolutions: Array<{
    characterId: string
    resolution: string
    futureRole: 'ally' | 'enemy' | 'neutral' | 'departed'
  }>
}> {
  const request = {
    storyline: {
      id: storyline.id,
      title: storyline.title,
      theme: storyline.theme,
      stakes: storyline.stakes,
    },
    journeySummary,
    keyCharacters,
    keyChoices,
  }

  const result = await generateStorylineClimaxFn(request)
  return result.data as {
    situation: GeneratedSituation
    epilogue: {
      success: {
        narrative: string
        rewards: Record<string, unknown>
        newOpportunities: string[]
      }
      failure: {
        narrative: string
        consequences: Record<string, unknown>
        recoveryPath: string
      }
    }
    characterResolutions: Array<{
      characterId: string
      resolution: string
      futureRole: 'ally' | 'enemy' | 'neutral' | 'departed'
    }>
  }
}

// ===================================
// DYNAMIC BACKSTORY GENERATION
// ===================================

export interface GeneratedBackstory {
  backstory: string
  funFacts: string[]
  locationName: string
  quirkyDetail: string
  motivation: string
}

/**
 * Generate a unique AI backstory for a new game
 */
export async function generateBackstoryViaCloud(
  difficulty: 'paper_hands' | 'diamond_hands' | 'gigabrain',
  startingAmount: number,
  goalAmount: number,
  daysLimit: number
): Promise<GeneratedBackstory> {
  const result = await generateBackstoryFn({
    difficulty,
    startingAmount,
    goalAmount,
    daysLimit,
  })

  return result.data as GeneratedBackstory
}

// ===================================
// GAME RECAP GENERATION
// ===================================

export interface GeneratedRecap {
  headline: string
  summary: string
  whatWentWell: string
  whatWentWrong: string
  deathBlow: string | null
  finalQuote: string
  rating: string
}

/**
 * Generate an AI recap of the completed game
 */
export async function generateGameRecapViaCloud(
  gameResult: 'won' | 'lost',
  endReason: string,
  startingAmount: number,
  finalAmount: number,
  goalAmount: number,
  decisionCount: number,
  daysSurvived: number,
  keyEvents: string[],
  bestTrade: string,
  worstTrade: string,
  winRate: number,
  reputation: number,
  degenScore: number,
  trustScore: number
): Promise<GeneratedRecap> {
  const result = await generateGameRecapFn({
    gameResult,
    endReason,
    startingAmount,
    finalAmount,
    goalAmount,
    decisionCount,
    daysSurvived,
    keyEvents,
    bestTrade,
    worstTrade,
    winRate,
    reputation,
    degenScore,
    trustScore,
  })

  return result.data as GeneratedRecap
}
